import { Link } from 'react-router-dom'
import { Instagram, Facebook, Globe, Mail } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'
import { translations } from '../lib/translations'
import { TERMS_EMAIL, TERMS_WEBSITE } from '../content/termsContent'

type AppFooterProps = {
  language?: string
  variant?: string
  maxWidth?: string
  bordered?: boolean
  className?: string
}

// Outbound social profiles. Defaults point at the main site so nothing 404s;
// swap in the real handles when they exist.
const SOCIAL_LINKS = [
  { key: 'instagram', label: 'Instagram', icon: Instagram, href: TERMS_WEBSITE },
  { key: 'facebook', label: 'Facebook', icon: Facebook, href: TERMS_WEBSITE },
  { key: 'website', label: 'Storyfy.ge', icon: Globe, href: TERMS_WEBSITE },
]

const AppFooter = ({
  language: languageProp,
  variant = 'default',
  maxWidth = 'max-w-7xl',
  bordered = true,
  className = '',
}: AppFooterProps) => {
  const { language: contextLanguage } = useLanguage()
  const language = languageProp || contextLanguage
  const t = translations[language] || translations.en

  const isOnDark = variant === 'onDark'
  // Transparent background so the page's own backdrop (the animated gradient on
  // the public pages, warm paper elsewhere) shows through — only a hairline
  // top border separates the footer.
  const footerClass = bordered
    ? isOnDark
      ? 'border-t border-white/10'
      : 'border-t border-[#1b1033]/10 dark:border-white/10'
    : ''

  const wordmarkClass = isOnDark
    ? 'text-white'
    : 'text-[#1b1033] dark:text-[#f4f0ff]'
  const taglineClass = isOnDark
    ? 'text-sm text-gray-400'
    : 'text-sm text-[#4a3d66]/80 dark:text-[#c3b8dd]'
  const headingClass = isOnDark
    ? 'text-xs font-semibold uppercase tracking-wider text-gray-500'
    : 'text-xs font-semibold uppercase tracking-wider text-[#4a3d66]/70 dark:text-[#c3b8dd]/70'
  // Mockup .f-col a: links slide 4px to the right on hover.
  const navLinkClass = isOnDark
    ? 'inline-block text-sm text-gray-300 transition-[color,transform] duration-200 hover:translate-x-1 hover:text-white motion-reduce:transition-none motion-reduce:hover:translate-x-0'
    : 'inline-block text-sm text-[#4a3d66] transition-[color,transform] duration-200 hover:translate-x-1 hover:text-[#ff4d8d] motion-reduce:transition-none motion-reduce:hover:translate-x-0 dark:text-[#c3b8dd] dark:hover:text-white'
  const metaClass = isOnDark
    ? 'text-xs text-gray-400'
    : 'text-xs text-[#4a3d66]/80 dark:text-[#c3b8dd]'
  // Mockup .f-socials a: rounded squares that fill with the hot gradient and lift.
  const socialBtnClass = isOnDark
    ? 'inline-flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-gray-300 transition-all duration-200 hover:-translate-y-0.5 hover:border-transparent hover:bg-grad-hot hover:text-white hover:shadow-[0_8px_18px_rgba(255,77,141,0.35)] motion-reduce:transition-none motion-reduce:hover:translate-y-0'
    : 'inline-flex h-9 w-9 items-center justify-center rounded-xl border border-[#1b1033]/10 bg-[#1b1033]/[0.03] text-[#4a3d66] transition-all duration-200 hover:-translate-y-0.5 hover:border-transparent hover:bg-grad-hot hover:text-white hover:shadow-[0_8px_18px_rgba(255,77,141,0.35)] motion-reduce:transition-none motion-reduce:hover:translate-y-0 dark:border-white/10 dark:bg-white/5 dark:text-[#c3b8dd] dark:hover:text-white'

  const legalLinks = [
    { to: '/terms', label: t.footerTerms },
    { to: '/privacy', label: t.footerPrivacy },
    { to: '/refund', label: t.footerRefund },
  ]

  return (
    <footer className={`mt-auto ${footerClass} ${className}`.trim()}>
      <div className={`${maxWidth} mx-auto px-4 sm:px-6 lg:px-8 py-12`}>
        <div className="grid grid-cols-2 gap-x-8 gap-y-10 md:grid-cols-12">
          {/* Brand */}
          <div className="col-span-2 md:col-span-5">
            <p
              className={`font-display text-lg font-bold leading-none tracking-tight ${wordmarkClass}`}
            >
              Storyfy<span className="text-[#ff4d8d]">.</span>
            </p>
            <p className={`mt-3 max-w-xs ${taglineClass}`}>
              {t.footerTagline || t.footerPoweredBy}
            </p>
            <div className="mt-5 flex items-center gap-2.5">
              {SOCIAL_LINKS.map(({ key, label, icon: Icon, href }) => (
                <a
                  key={key}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  title={label}
                  className={socialBtnClass}
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Legal */}
          <nav
            aria-label={t.footerLegal}
            className="col-span-1 md:col-span-3 md:col-start-7"
          >
            <h3 className={headingClass}>{t.footerLegal}</h3>
            <ul className="mt-4 space-y-2.5">
              {legalLinks.map(({ to, label }) => (
                <li key={to}>
                  <Link to={to} className={navLinkClass}>
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Company / Contact */}
          <nav
            aria-label={t.footerCompany}
            className="col-span-1 md:col-span-3"
          >
            <h3 className={headingClass}>{t.footerCompany}</h3>
            <ul className="mt-4 space-y-2.5">
              <li>
                <a
                  href={`mailto:${TERMS_EMAIL}`}
                  className={`inline-flex items-center gap-2 ${navLinkClass}`}
                >
                  <Mail className="h-4 w-4" />
                  {t.footerContactUs}
                </a>
              </li>
              <li>
                <a
                  href={TERMS_WEBSITE}
                  target="_blank"
                  rel="noreferrer"
                  className={`inline-flex items-center gap-2 ${navLinkClass}`}
                >
                  <Globe className="h-4 w-4" />
                  {t.footerWebsite}
                </a>
              </li>
            </ul>
          </nav>
        </div>

        {/* Bottom bar */}
        <div
          className={`mt-10 flex flex-col items-start gap-2 border-t pt-6 sm:flex-row sm:items-center sm:justify-between ${
            isOnDark ? 'border-white/10' : 'border-[#1b1033]/10 dark:border-white/10'
          }`}
        >
          <p className={metaClass}>{t.footerCopyright}</p>
        </div>
      </div>
    </footer>
  )
}

export default AppFooter
